import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import { logEvent } from '../../lib/analytics';
import { highlightEmotionBrackets, SM_BREAKPOINT } from '../../lib/utils';
import { useVoiceDemo } from '../../providers/VoiceDemoContext';

const EDITOR_STYLES =
  'h-[150px] @sm:h-[250px] overflow-y-auto text-left text-[1rem] @sm:text-xl font-[400] leading-tight outline-none w-full';
export const MAX_EDITOR_CONTENT_LENGTH = 500;

export function EditorComponent() {
  const {
    currentContent: content,
    handleUpdateTextContent: onTextContentChange,
  } = useVoiceDemo();
  const editorRef = useRef<HTMLDivElement>(null);
  const isComposingRef = useRef(false);
  const [localContent, setLocalContent] = useState(content);

  useEffect(() => {
    setLocalContent(content);
  }, [content]);

  // Handle input changes from contenteditable
  const handleInput = useCallback(() => {
    if (!editorRef.current || isComposingRef.current) return;

    // Save cursor position before getting text
    const selection = window.getSelection();
    let cursorOffset = 0;
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(editorRef.current);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      cursorOffset = preCaretRange.toString().length;
    }

    // Get plain text from contenteditable
    const plainText = editorRef.current.innerText;

    // Update local content
    setLocalContent(plainText);

    // Update with highlighted content
    requestAnimationFrame(() => {
      if (!editorRef.current) return;

      const highlighted = highlightEmotionBrackets(plainText);

      editorRef.current.innerHTML = highlighted;

      // Restore cursor position
      restoreCursor(editorRef.current, cursorOffset);
    });
  }, []);

  // Restore cursor to the correct position
  const restoreCursor = (element: HTMLElement, offset: number) => {
    const selection = window.getSelection();
    if (!selection) return;

    let currentOffset = 0;
    let found = false;

    const traverseNodes = (node: Node): boolean => {
      if (found) return true;

      if (node.nodeType === Node.TEXT_NODE) {
        const textLength = node.textContent?.length ?? 0;
        if (currentOffset + textLength >= offset) {
          const range = document.createRange();
          range.setStart(node, Math.min(offset - currentOffset, textLength));
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
          found = true;
          return true;
        }
        currentOffset += textLength;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        for (const childNode of node.childNodes) {
          if (traverseNodes(childNode)) return true;
        }
      }
      return false;
    };

    traverseNodes(element);

    // If we didn't find the position, put cursor at end
    if (!found && element.childNodes.length > 0) {
      const range = document.createRange();
      range.selectNodeContents(element);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  // Handle blur event
  const handleBlur = useCallback(() => {
    if (localContent !== content) {
      onTextContentChange(localContent.trim());
      logEvent('editor_interaction', { action: 'manual_text_edit' });
    }
  }, [localContent, content, onTextContentChange]);

  // Initialize content on mount
  const displayContent = highlightEmotionBrackets(content);

  useEffect(() => {
    if (editorRef.current && window.screen.width > SM_BREAKPOINT) {
      editorRef.current.focus();
      // Move cursor to end
      const range = document.createRange();
      const selection = window.getSelection();
      if (editorRef.current.childNodes.length > 0) {
        range.selectNodeContents(editorRef.current);
        range.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }
  }, []);

  return (
    <div
      ref={editorRef}
      contentEditable
      suppressContentEditableWarning
      onInput={handleInput}
      onBlur={handleBlur}
      onCompositionStart={() => {
        isComposingRef.current = true;
      }}
      onCompositionEnd={() => {
        isComposingRef.current = false;
        handleInput();
      }}
      className={clsx('resize-none', EDITOR_STYLES)}
      style={{
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
      spellCheck="false"
      aria-label="Text to convert to speech"
      dangerouslySetInnerHTML={{ __html: displayContent || '' }}
      data-placeholder={
        !content ? 'Enter text to convert to speech...' : undefined
      }
    />
  );
}

